import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { delAuth } from '@/api/ApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import { FundCampaignDtoIn } from '@/models/in/FundCampaignDtoIn';
import addIcon from '@/assets/icons/add.svg';
import editIcon from '@/assets/icons/edit.svg';
import deleteIcon from '@/assets/icons/trash.svg';
import externalLinkIcon from '@/assets/icons/external-link.svg';
import EditOrCreateCampaignDialog from '../EditOrCreateCampaignDialog';
import { campaignsUrl, hasStarted, shortenUrl } from '../fundCampaigns';
import styles from './FundBlade.module.css';

interface CampaignsTabProps {
  fundId: number;
}

/**
 * A fund's campaigns (HAT-1121).
 *
 * Each row here is a Nedarim decode rule: the campaign number is the value that places the campaign's
 * donations in this fund. Deleting a campaign therefore also removes that placement rule, which is why
 * the confirmation says so rather than asking a bare "are you sure".
 */
const CampaignsTab = ({ fundId }: CampaignsTabProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentUser } = useAuthContext();

  const url = campaignsUrl(fundId);
  const { data: campaigns, isLoading } = useGetAuthApi<FundCampaignDtoIn[]>(url);

  const [editorOpen, setEditorOpen] = useState(false);
  const [campaignToEdit, setCampaignToEdit] = useState<FundCampaignDtoIn | null>(null);
  const [campaignToDelete, setCampaignToDelete] = useState<FundCampaignDtoIn | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: [url] });

  const openEditor = (campaign: FundCampaignDtoIn | null) => {
    setCampaignToEdit(campaign);
    setEditorOpen(true);
  };

  const confirmDelete = async () => {
    if (!campaignToDelete) return;
    setDeleteError(null);
    setIsDeleting(true);
    try {
      await delAuth(`${url}/${campaignToDelete.id}`, { Authorization: `Bearer ${currentUser?.token}` });
      setCampaignToDelete(null);
      refresh();
    } catch {
      setDeleteError(t('fundsManagement.campaigns.deleteFailed'));
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (value: string | null) =>
    (value ? new Date(value.slice(0, 10)).toLocaleDateString('he-IL') : '');

  const rows = campaigns ?? [];

  return (
    <div>
      <div className={styles.tabToolbar}>
        <button type="button" className={styles.editIconBtn} onClick={() => openEditor(null)}>
          <img src={addIcon} alt="" />
          {t('fundsManagement.campaigns.add')}
        </button>
      </div>

      {isLoading ? (
        <p className={styles.emptyText}>{t('loading')}...</p>
      ) : rows.length === 0 ? (
        <p className={styles.emptyText}>{t('fundsManagement.campaigns.empty')}</p>
      ) : (
        <div className={styles.personList}>
          {rows.map((campaign) => {
            const started = hasStarted(campaign);
            return (
              <div className={styles.campaignRow} key={campaign.id}>
                <div className={styles.campaignSummary}>
                  <div className={styles.campaignHeader}>
                    <span className={styles.personName}>{campaign.name}</span>
                    <span className={`${styles.featureBadge} ${started ? styles.featureBadgeOn : styles.featureBadgeOff}`}>
                      {started ? t('fundsManagement.campaigns.statusActive') : t('fundsManagement.campaigns.statusPlanned')}
                    </span>
                  </div>
                  <span className={styles.personMeta}>
                    <span dir="ltr">{t('fundsManagement.campaigns.numberShort', { number: campaign.campaignNumber })}</span>
                    <span className={styles.metaDot}>•</span>
                    <span>{t('fundsManagement.campaigns.startsOn', { date: formatDate(campaign.startDate) })}</span>
                    {campaign.url && (
                      <>
                        <span className={styles.metaDot}>•</span>
                        <a
                          href={campaign.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          dir="ltr"
                          className={`body-md-link text-(--font-link) ${styles.campaignLink}`}
                        >
                          <img src={externalLinkIcon} alt="" />
                          {shortenUrl(campaign.url)}
                        </a>
                      </>
                    )}
                  </span>
                </div>

                <div className={styles.rowActions}>
                  <button type="button" className={styles.rowEditBtn} onClick={() => openEditor(campaign)}>
                    <img src={editIcon} alt="" />
                    {t('fundsManagement.editButton')}
                  </button>
                  <button
                    type="button"
                    className={styles.removeBtn}
                    aria-label={t('fundsManagement.campaigns.deleteTitle')}
                    onClick={() => { setDeleteError(null); setCampaignToDelete(campaign); }}
                  >
                    <img src={deleteIcon} alt="" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {deleteError && <p className={styles.actionError}>{deleteError}</p>}

      <EditOrCreateCampaignDialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSaved={refresh}
        fundId={fundId}
        campaignToEdit={campaignToEdit}
      />

      <ConfirmationDialog
        open={campaignToDelete !== null}
        onOpenChange={(isOpen) => { if (!isOpen) setCampaignToDelete(null); }}
        title={t('fundsManagement.campaigns.deleteTitle')}
        message={t('fundsManagement.campaigns.deleteConfirm', {
          name: campaignToDelete?.name ?? '',
          number: campaignToDelete?.campaignNumber ?? '',
        })}
        confirmText="fundsManagement.campaigns.deleteConfirmButton"
        cancelText="fundsManagement.cancel"
        variant="danger"
        isLoading={isDeleting}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default CampaignsTab;
